import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, HandCoins, Trash2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import FanWall from "@/components/FanWall";
import AdminQuestions from "@/components/AdminQuestions";
import { api, formatApiError } from "@/lib/api";

function StatCard({ icon: Icon, value, label }) {
  return (
    <div className="rounded-2xl border border-border bg-cream p-6">
      <Icon className="h-6 w-6 text-terracotta" />
      <p className="mt-3 font-serif text-3xl font-bold text-forest">{value}</p>
      <p className="text-sm text-forest/60">{label}</p>
    </div>
  );
}

function Donations() {
  const [donations, setDonations] = useState([]);
  const [form, setForm] = useState({ donor_name: "", amount: "", note: "" });

  const load = () => api.get("/admin/donations").then((r) => setDonations(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.donor_name.trim() || !form.amount) return toast.error("Add donor name and amount.");
    try {
      await api.post("/admin/donations", { donor_name: form.donor_name, amount: parseFloat(form.amount), note: form.note || null });
      setForm({ donor_name: "", amount: "", note: "" });
      load();
      toast.success("Donation recorded.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/donations/${id}`);
    load();
  };

  const total = donations.reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div className="rounded-2xl border border-border bg-cream p-7">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl font-semibold text-forest">Donations</h3>
        <span className="rounded-full bg-terracotta/10 px-3 py-1 text-sm font-medium text-terracotta">
          ${total.toLocaleString()} total
        </span>
      </div>

      <form onSubmit={add} className="mt-5 grid gap-3 sm:grid-cols-[1fr_120px_auto]">
        <input value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} placeholder="Donor name"
          data-testid="donation-name-input" className="rounded-xl border border-input bg-background px-4 py-2.5 outline-none focus:border-terracotta" />
        <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="$ Amount" type="number" min="0" step="0.01"
          data-testid="donation-amount-input" className="rounded-xl border border-input bg-background px-4 py-2.5 outline-none focus:border-terracotta" />
        <button data-testid="donation-add-button" className="flex items-center justify-center gap-1 rounded-xl bg-terracotta px-5 py-2.5 font-medium text-cream hover:bg-terracotta-dark">
          <Plus className="h-4 w-4" /> Add
        </button>
      </form>

      <div className="mt-5 divide-y divide-border">
        {donations.length === 0 ? (
          <p className="py-6 text-center text-sm text-forest/50">No donations recorded yet.</p>
        ) : donations.map((d) => (
          <div key={d.id} className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-forest">{d.donor_name}</p>
              {d.note && <p className="text-xs text-forest/50">{d.note}</p>}
            </div>
            <div className="flex items-center gap-4">
              <span className="font-serif font-semibold text-terracotta">${Number(d.amount).toLocaleString()}</span>
              <button onClick={() => remove(d.id)} data-testid={`donation-delete-${d.id}`} className="text-forest/40 hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { authed } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => { if (authed === false) navigate("/admin/login"); }, [authed, navigate]);
  useEffect(() => {
    if (authed) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [authed]);

  const deleteMessage = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    toast.success("Message deleted.");
    setStats((s) => (s ? { ...s, total_messages: s.total_messages - 1 } : s));
  };

  if (authed !== true) {
    return <div className="grid min-h-[100dvh] place-items-center text-forest/60">Loading…</div>;
  }

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-5 py-12">
        <h1 className="font-serif text-4xl font-bold text-forest" data-testid="admin-dashboard-title">Admin Dashboard</h1>
        <p className="mt-1 text-forest/60">Moderate tributes and track GoFundMe donations.</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <StatCard icon={MessageSquare} value={stats?.total_messages ?? "—"} label="Messages" />
          <StatCard icon={HandCoins} value={stats?.total_donations ?? "—"} label="Donations" />
          <StatCard icon={HandCoins} value={`$${(stats?.total_donation_amount ?? 0).toLocaleString()}`} label="Total raised" />
        </div>

        <div className="mt-10">
          <Donations />
        </div>

        <div className="mt-10">
          <AdminQuestions />
        </div>

        <div className="mt-12">
          <h2 className="mb-6 font-serif text-2xl font-semibold text-forest">All Tributes</h2>
          <FanWall admin onAdminDelete={deleteMessage} />
        </div>
      </div>
    </Layout>
  );
}
